import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Clock, Copy, ImagePlus, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, img, inr, load, StatusBadge } from "@/lib/api";
import { Cta } from "@/components/Cta";
import { Totals } from "@/components/BagDrawer";

const STEPS = ["Order placed", "Pay by UPI", "Verification", "Confirmed"];
const stepOf = (o) => (o.payment_status === "paid" || o.payment_status === "refunded" ? 3 : o.payment_status === "under_verification" ? 2 : 1);

const Stepper = ({ o }) => {
  const s = stepOf(o);
  return (
    <ol className="grid grid-cols-4 gap-2 mb-8" data-testid="payment-stepper">
      {STEPS.map((t, i) => (
        <li key={t}>
          <div className={`h-1.5 rounded-full ${i <= s ? "bg-berry" : "bg-plum/10"}`} />
          <p className={`mt-2 text-[12px] sm:text-[13px] font-bold ${i <= s ? "text-plum" : "text-plum/45"}`}>{t}</p>
        </li>
      ))}
    </ol>
  );
};

const UtrForm = ({ o, token, onDone }) => {
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[A-Za-z0-9]{10,22}$/.test(utr.replace(/\s/g, ""))) return toast.error("Enter the 12-digit UTR / UPI reference number from your UPI app");
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${o.order_number}/utr`, { token, utr });
      toast.dismiss();
      toast.success("Thank you! We're verifying your payment.");
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="utr-form">
      <label className="block text-[15px] font-bold">Paid already? Enter your UTR / UPI reference number</label>
      <div className="flex flex-col sm:flex-row gap-3">
        <input value={utr} onChange={(e) => setUtr(e.target.value)} className="rh-input sm:flex-1 tracking-[0.12em] font-bold" placeholder="e.g. 412345678901" inputMode="numeric" maxLength={22} data-testid="utr-input" />
        <Cta type="submit" label="Submit UTR" state={busy ? "loading" : "idle"} testId="utr-submit" />
      </div>
      <p className="text-[13px] text-plum/60">Find it in your UPI app under transaction details (often called UTR, UPI Ref No. or Transaction ID).</p>
    </form>
  );
};

const Screenshot = ({ o, token, onDone }) => {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("token", token);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/orders/${o.order_number}/screenshot`, fd);
      toast.success("Screenshot uploaded");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <label className={`flex items-center gap-3 rounded-[14px] border border-dashed border-plum/25 p-4 cursor-pointer hover:border-plum transition-colors ${busy ? "opacity-60" : ""}`} data-testid="screenshot-upload">
      <ImagePlus className="w-5 h-5 text-berry" />
      <span className="text-[14px] font-semibold">{o.has_screenshot ? "Screenshot received — upload another (optional)" : busy ? "Uploading…" : "Upload payment screenshot (optional)"}</span>
      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={up} disabled={busy} data-testid="screenshot-input" />
    </label>
  );
};

const PayPanel = ({ o, token, reload, setO }) => {
  const copy = () => navigator.clipboard?.writeText(o.upi_id).then(() => toast.success("UPI ID copied"));
  return (
    <div className="space-y-8">
      {o.payment_status === "rejected" && (
        <div className="flex gap-3 rounded-[16px] bg-[#F6DDE2] text-ruby p-4" data-testid="payment-rejected-notice">
          <AlertCircle className="w-5 h-5 flex-none mt-0.5" />
          <p className="text-[15px] font-semibold">{o.rejection_reason}</p>
        </div>
      )}
      <div className="grid sm:grid-cols-[auto_1fr] gap-8 items-center">
        <div className="mx-auto rounded-[22px] bg-white p-4 border border-plum/10 shadow-[0_20px_50px_-30px_rgba(48,32,45,.5)]">
          <img src={o.qr} alt="UPI QR code for this order" className="w-[240px] h-[240px] sm:w-[260px] sm:h-[260px]" data-testid="upi-qr-image" />
          <p className="text-center text-[12px] font-bold tracking-[0.16em] uppercase text-plum/60 mt-2">Scan with any UPI app</p>
        </div>
        <div className="space-y-5">
          <div>
            <p className="text-[14px] font-bold text-plum/60">Amount to pay</p>
            <p className="font-display text-[52px] font-semibold leading-none mt-1" data-testid="payment-amount">{inr(o.total)}</p>
          </div>
          <dl className="space-y-2 text-[15px]">
            <div className="flex gap-2"><dt className="text-plum/60 w-24">Order ID</dt><dd className="font-extrabold" data-testid="payment-order-id">{o.order_number}</dd></div>
            <div className="flex gap-2"><dt className="text-plum/60 w-24">Pay to</dt><dd className="font-bold">{o.merchant_name}</dd></div>
            <div className="flex gap-2 items-center"><dt className="text-plum/60 w-24">UPI ID</dt><dd className="font-bold break-all" data-testid="payment-upi-id">{o.upi_id}</dd>
              <button onClick={copy} className="ml-1 h-9 px-3 rounded-[10px] bg-plum/5 hover:bg-plum/10 text-[12px] font-extrabold uppercase tracking-wider flex items-center gap-1.5" data-testid="copy-upi-button"><Copy className="w-3.5 h-3.5" />Copy</button>
            </div>
          </dl>
          <Cta href={o.upi_uri} label="Open UPI app" variant="berry" icon={<Smartphone className="w-[18px] h-[18px]" />} testId="open-upi-app" className="sm:hidden w-full" />
        </div>
      </div>
      <div className="rounded-[16px] bg-lilac p-5 text-[15px] leading-relaxed" data-testid="payment-instructions">{o.instructions}</div>
      <UtrForm o={o} token={token} onDone={setO} />
      <Screenshot o={o} token={token} onDone={reload} />
    </div>
  );
};

export default function Payment() {
  const { number } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || load("rh_orders", {})[number];
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const reload = useCallback(() => api.get(`/orders/${number}`, { params: { token } }).then((r) => setO(r.data)).catch((e) => setErr(errMsg(e))), [number, token]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (o?.payment_status !== "under_verification") return;
    const t = setInterval(reload, 20000);
    return () => clearInterval(t);
  }, [o?.payment_status, reload]);

  if (err) return <main className="container-rh py-28 text-center"><p className="font-display text-[40px]">{err}</p><p className="text-plum/70 mt-3">Please use the link from your order confirmation.</p></main>;
  if (!o) return <main className="container-rh py-16"><div className="h-[520px] rounded-[22px] bg-blush animate-pulse" /></main>;

  return (
    <main className="container-rh py-10 lg:py-16" data-testid="payment-page">
      <div className="flex flex-wrap items-center gap-3 mb-3"><p className="eyebrow text-berry">Order {o.order_number}</p><StatusBadge status={o.payment_status} testId="payment-status-badge" /></div>
      <h1 className="font-display font-medium text-[40px] sm:text-[54px] leading-none mb-10">
        {o.status === "cancelled" ? "This order was cancelled." : o.payment_status === "paid" ? "Payment confirmed." : o.payment_status === "under_verification" ? "We're verifying your payment." : "Complete your payment."}
      </h1>
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <section className="lg:col-span-7 rounded-[22px] bg-ivory border border-plum/10 p-6 sm:p-10">
          <Stepper o={o} />
          {o.status === "cancelled" ? (
            <p className="text-[16px]">If you have already paid, please contact us with your order ID and we will help you right away.</p>
          ) : o.payment_status === "paid" || o.payment_status === "refunded" ? (
            <div className="flex gap-4 items-start" data-testid="payment-confirmed">
              <CheckCircle2 className="w-10 h-10 text-emerald flex-none" />
              <div>
                <p className="font-display text-[30px] font-semibold">Thank you — your piece is on its way soon.</p>
                <p className="text-plum/70 mt-2">We received {inr(o.total)} for order {o.order_number}. You'll receive tracking details once it ships.</p>
                {o.tracking?.number && <p className="mt-4 font-bold">Tracking: {o.tracking.carrier} {o.tracking.number} {o.tracking.url && <a href={o.tracking.url} target="_blank" rel="noreferrer" className="underline ml-2">Track</a>}</p>}
              </div>
            </div>
          ) : o.payment_status === "under_verification" ? (
            <div className="space-y-6" data-testid="payment-under-verification">
              <div className="flex gap-4 items-start">
                <Clock className="w-10 h-10 text-sapphire flex-none" />
                <div>
                  <p className="font-display text-[28px] font-semibold">Payment under verification</p>
                  <p className="text-plum/75 mt-2">We received your UTR <b>{o.utr}</b>. Our team checks every payment in our UPI account before confirming. This page updates automatically.</p>
                </div>
              </div>
              <Screenshot o={o} token={token} onDone={reload} />
            </div>
          ) : (
            <PayPanel o={o} token={token} reload={reload} setO={setO} />
          )}
        </section>
        <aside className="lg:col-span-5 rounded-[22px] bg-white border border-plum/10 p-6 sm:p-8">
          <h2 className="font-display text-[26px] font-semibold mb-3">Your order</h2>
          <ul className="divide-y divide-plum/10 mb-5">
            {o.items.map((l) => (
              <li key={l.product_id + l.variant} className="flex gap-4 py-4">
                <img src={img(l.image)} alt="" className="w-16 h-20 rounded-[12px] object-cover" />
                <div className="flex-1"><p className="font-display text-[20px] font-semibold leading-tight">{l.name}</p><p className="text-[14px] text-plum/70">{l.variant ? `${l.variant} · ` : ""}Qty {l.quantity}</p></div>
                <p className="font-bold">{inr(l.line_total)}</p>
              </li>
            ))}
          </ul>
          <Totals quote={o} />
          <div className="mt-6 pt-5 border-t border-plum/10 text-[14px] text-plum/75 leading-relaxed">
            <p className="font-bold text-plum mb-1">Delivering to</p>
            {o.customer.name}, {o.customer.address}, {o.customer.city}, {o.customer.state} {o.customer.pincode}<br />{o.customer.mobile}
          </div>
        </aside>
      </div>
    </main>
  );
}
